/**
 * The one rename control (platform design §5.4): `PUT .../tracked-accounts/{id}/label`, its own
 * route rather than a field on `TrackingConfigIn` (`api/src/socialsrc_api/routers/tracking.py`'s
 * `write_tracked_account_label`), so it never replaces the tracking configuration. An empty input
 * clears the label -- `null`, never `""` -- and the display falls back to the handle.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';

import { FormDialog, fieldControlProps, fieldErrorOf } from '../../../chrome/form';
import { IconHint } from '../../../chrome/hint';
import { Button } from '../../../ui/button';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { useRenameAccount } from './api/queries';
import { am } from './messages';

export interface RenameAccountProps {
  campaignId: string;
  trackedAccountId: string;
  label: string | null;
  handle: string | null;
  // Given, the caller opens the dialog from its own menu and no trigger renders here.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function RenameAccount(props: RenameAccountProps): ReactNode {
  const { campaignId, trackedAccountId, label, handle } = props;
  const rename = useRenameAccount(campaignId, trackedAccountId);
  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = props.open !== undefined;
  const open = props.open ?? ownOpen;
  const setOpen = props.onOpenChange ?? setOwnOpen;
  const [value, setValue] = useState(label ?? '');
  const inputId = useId();
  const errorId = useId();
  const failure = rename.error?.failure ?? null;
  const labelError = fieldErrorOf(failure, 'label');

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (next) {
      setValue(label ?? '');
    } else {
      rename.reset();
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): Promise<unknown> {
    event.preventDefault();
    const trimmed = value.trim();
    return rename.mutateAsync({ label: trimmed === '' ? null : trimmed });
  }

  return (
    <>
      {!controlled && (
        <IconHint label={am('rename.trigger')}>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            onClick={() => handleOpenChange(true)}
          >
            <Pencil aria-hidden />
          </Button>
        </IconHint>
      )}
      <FormDialog
        open={open}
        onOpenChange={handleOpenChange}
        title={am('rename.title')}
        submitLabel={am('rename.submit')}
        pending={rename.isPending}
        failure={rename.error ?? null}
        onSubmit={submit}
      >
        <Field data-invalid={labelError !== null || undefined}>
          <FieldLabel htmlFor={inputId}>{am('rename.label')}</FieldLabel>
          <Input
            id={inputId}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={handle ? `@${handle}` : undefined}
            {...fieldControlProps(failure, 'label', errorId)}
          />
          <FieldError id={errorId}>{labelError}</FieldError>
        </Field>
      </FormDialog>
    </>
  );
}
